import {
    AREA_GROUP_MODE_AUTO_BY_SPOTS,
    AREA_GROUP_MODE_FIXED,
    AREA_GROUP_MODE_USER_CREATED,
    AreaGroupMode,
    AreaLanguageOption,
    AreaZones,
    BLE_SCAN_MODE_DONGLE_ONLY,
    BLE_SCAN_MODE_DONGLE_OR_PHONE,
    BLE_SCAN_MODE_NONE,
    BleScanMode,
    BoundariesZone,
    Zone
} from "../../types";

export const BASE_LANGUAGE_OPTIONS: AreaLanguageOption[] = [
    {code: "DA", label: "Danish", flagUrl: "https://flagcdn.com/dk.svg"},
    {code: "EN", label: "English", flagUrl: "https://flagcdn.com/gb.svg"}
];

const LANGUAGE_FLAG_OVERRIDES: Record<string, string> = {
    AF: "za",
    AM: "et",
    AR: "sa",
    AZ: "az",
    BG: "bg",
    BN: "bd",
    BS: "ba",
    CA: "es",
    CS: "cz",
    CY: "gb",
    DA: "dk",
    DE: "de",
    EL: "gr",
    EN: "gb",
    ES: "es",
    ET: "ee",
    FA: "ir",
    FI: "fi",
    FR: "fr",
    GA: "ie",
    HE: "il",
    HI: "in",
    HR: "hr",
    HU: "hu",
    HY: "am",
    ID: "id",
    IS: "is",
    IT: "it",
    JA: "jp",
    KA: "ge",
    KK: "kz",
    KO: "kr",
    LT: "lt",
    LV: "lv",
    MK: "mk",
    MS: "my",
    MT: "mt",
    NL: "nl",
    NO: "no",
    PL: "pl",
    PT: "pt",
    RO: "ro",
    RU: "ru",
    SK: "sk",
    SL: "si",
    SQ: "al",
    SR: "rs",
    SV: "se",
    SW: "tz",
    TH: "th",
    TR: "tr",
    UK: "ua",
    UR: "pk",
    VI: "vn",
    ZH: "cn"
};

const BOUNDARIES_ZONE_NAME = "Boundaries";
const BLE_SCAN_OPTIONS: BleScanMode[] = [BLE_SCAN_MODE_DONGLE_OR_PHONE, BLE_SCAN_MODE_DONGLE_ONLY, BLE_SCAN_MODE_NONE];
export const AREA_GROUP_MODE_OPTIONS: AreaGroupMode[] = [AREA_GROUP_MODE_FIXED, AREA_GROUP_MODE_AUTO_BY_SPOTS, AREA_GROUP_MODE_USER_CREATED];
const AREA_GROUP_MODE_SET = new Set<string>(AREA_GROUP_MODE_OPTIONS);

export const FALLBACK_LANGUAGE = "EN";

export const normalizeLanguageCode = (code?: string) => (code || "").trim().split("-")[0].toUpperCase();

export const resolveSuggestedFlagUrl = (languageCode: string) => {
    const normalizedCode = normalizeLanguageCode(languageCode);
    const countryCode = LANGUAGE_FLAG_OVERRIDES[normalizedCode];
    return countryCode ? `https://flagcdn.com/${countryCode}.svg` : undefined;
};

export const buildLanguageCatalog = (): AreaLanguageOption[] => {
	const map = new Map<string, AreaLanguageOption>();

    BASE_LANGUAGE_OPTIONS.forEach((option) => {
        const normalizedCode = normalizeLanguageCode(option.code);
        map.set(normalizedCode, {
            code: normalizedCode,
            label: option.label,
            flagUrl: option.flagUrl || resolveSuggestedFlagUrl(normalizedCode)
        });
    });

	const displayNames = typeof Intl.DisplayNames === "function"
		? new Intl.DisplayNames(["en"], {type: "language"})
		: undefined;

	const candidateCodes = new Set<string>();
	Object.keys(LANGUAGE_FLAG_OVERRIDES).forEach((languageCode) => candidateCodes.add(normalizeLanguageCode(languageCode)));

    // Fallback for runtimes without Intl.supportedValuesOf("language"): probe 2-letter codes.
    if (displayNames) {
        const alphabet = "abcdefghijklmnopqrstuvwxyz";
        for (const first of alphabet) {
            for (const second of alphabet) {
                const probe = `${first}${second}`;
                const label = displayNames.of(probe);
                if (label && label.toLowerCase() !== probe) {
                    candidateCodes.add(normalizeLanguageCode(probe));
                }
            }
        }
    }

    candidateCodes.forEach((normalizedCode) => {
        if (!normalizedCode || map.has(normalizedCode)) {
            return;
        }
        const localizedLabel = displayNames?.of(normalizedCode.toLowerCase());
        map.set(normalizedCode, {
            code: normalizedCode,
            label: localizedLabel && localizedLabel.toLowerCase() !== normalizedCode.toLowerCase()
                ? localizedLabel
                : normalizedCode,
            flagUrl: resolveSuggestedFlagUrl(normalizedCode)
        });
    });

    const priorityOrder = new Map<string, number>([
        ["EN", 0],
        ["DA", 1]
    ]);

    return Array.from(map.values())
        .sort((a, b) => {
            const aPriority = priorityOrder.get(a.code);
            const bPriority = priorityOrder.get(b.code);
            if (aPriority !== undefined || bPriority !== undefined) {
                return (aPriority ?? Number.MAX_SAFE_INTEGER) - (bPriority ?? Number.MAX_SAFE_INTEGER);
            }
            return a.label.localeCompare(b.label);
        });
};

export const resolveBleScanMode = (value?: string): BleScanMode => {
    if (value && BLE_SCAN_OPTIONS.includes(value as BleScanMode)) {
        return value as BleScanMode;
    }
    return BLE_SCAN_MODE_DONGLE_OR_PHONE;
};

export const normalizeAreaGroupModes = (value?: string | string[] | null): AreaGroupMode[] => {
    const source = Array.isArray(value)
        ? value
        : (typeof value === "string" && value !== BLE_SCAN_MODE_NONE ? [value] : []);
    const filtered = source.filter((mode): mode is AreaGroupMode => AREA_GROUP_MODE_SET.has(mode));
    return Array.from(new Set(filtered));
};

const isBoundariesAmbientZone = (zone?: Zone) => {
    const name = (zone?.name || zone?.label || "").trim().toLowerCase();
    return name === BOUNDARIES_ZONE_NAME.toLowerCase();
};

const toLegacyBoundariesZone = (zone: Zone, existingBoundaries?: BoundariesZone): BoundariesZone => {
    return {
        ...zone,
        id: "boundaries",
        name: BOUNDARIES_ZONE_NAME,
        label: BOUNDARIES_ZONE_NAME,
        triggerToActivate: existingBoundaries?.triggerToActivate ?? (zone as BoundariesZone).triggerToActivate ?? false
    } as BoundariesZone;
};

const toBoundariesAmbientZone = (boundariesZone: BoundariesZone): Zone => {
    return {
        ...boundariesZone,
        name: BOUNDARIES_ZONE_NAME,
        label: BOUNDARIES_ZONE_NAME
    } as Zone;
};

export const migrateZonesForBoundariesSetting = (zones: AreaZones | undefined, useBoundariesZone: boolean): AreaZones | undefined => {
    if (!zones) {
        return zones;
    }

    let ambientZones: Zone[] = Array.isArray(zones.ambientZones) ? [...zones.ambientZones] : [];

    if (useBoundariesZone) {
        const ambientBoundariesIndex = ambientZones.findIndex((zone) => isBoundariesAmbientZone(zone));
        if (zones.boundaries) {
            if (ambientBoundariesIndex > -1) {
                ambientZones = ambientZones.filter((zone) => !isBoundariesAmbientZone(zone));
            }
            return {
                ...zones,
                ambientZones: ambientZones
            };
        }

        if (ambientBoundariesIndex > -1) {
            const ambientBoundaries = ambientZones[ambientBoundariesIndex];
            ambientZones.splice(ambientBoundariesIndex, 1);
            return {
                ...zones,
                boundaries: toLegacyBoundariesZone(ambientBoundaries, zones.boundaries),
                ambientZones: ambientZones
            };
        }

        return {
            ...zones,
            ambientZones: ambientZones
        };
    }

    if (!zones.boundaries) {
        return {
            ...zones,
            ambientZones: ambientZones
        };
    }

    const filteredAmbientZones = ambientZones.filter((zone) => !isBoundariesAmbientZone(zone));
    const boundariesAsAmbient = toBoundariesAmbientZone(zones.boundaries);
    const remainingZones: AreaZones = {
        ...zones
    };
    delete remainingZones.boundaries;

    return {
        ...remainingZones,
        ambientZones: [...filteredAmbientZones, boundariesAsAmbient]
    };
};
